import { useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useDescribeStringGroupQuery } from 'src/generated/graphql';
import {
  NewStringGroupMetadata,
  StringGroup,
  StringInfo,
  TranslationStatus,
} from '../types';

/**
 * Client id assigned to a group prefilled from the Create String Group modal.
 * A group authored in this UI is by definition LangHub-owned, so the prefilled
 * page must be editable (isLangHubClient → canEdit true).
 */
const NEW_STRING_GROUP_CLIENT_ID = 'langhub';

/**
 * Maps the form data handed over by the Create String Group modal to the
 * StringGroup shape the detail page components expect. Fields the form does
 * not know (version, counts, actors, translation state) get new-group
 * defaults: version 1, zero strings, empty progress, status Complete. These
 * defaults are local placeholders — nothing has been persisted server-side
 * yet (the create mutation is still a TODO in NamespaceDetailPage).
 */
export function mapNewStringGroupMetadata(
  newGroupMetadata: NewStringGroupMetadata,
): StringGroup {
  return {
    stringGroupId: newGroupMetadata.stringGroupId,
    namespaceId: newGroupMetadata.namespaceId,
    clientId: NEW_STRING_GROUP_CLIENT_ID,
    description: newGroupMetadata.description || undefined,
    teamName: newGroupMetadata.teamName || undefined,
    createdOn: newGroupMetadata.createdOn,
    createdBy: newGroupMetadata.createdBy,
    version: 1,
    cxFamilyId: newGroupMetadata.cxFamilyId,
    cxIds: newGroupMetadata.cxIds,
    sourceLocale: newGroupMetadata.sourceLocale,
    stringCount: 0,
    updatedOn: newGroupMetadata.createdOn,
    updatedBy: newGroupMetadata.createdBy,
    translationStatus: TranslationStatus.Complete,
    translationProgress: {
      completedLocales: [],
      inProgressLocales: [],
      failedLocales: [],
      cancelledLocales: [],
    },
  };
}

interface UseStringGroupDataResult {
  data: StringGroup | null;
  /**
   * The group's source strings, resolved from TRANSCOM in the same
   * describeStringGroup call (includeStrings=true) — no separate round trip.
   */
  strings: StringInfo[];
  loading: boolean;
  error: Error | null;
  /** Raw Apollo error preserved for extension inspection (e.g. errorType). */
  apolloError: ApolloError | undefined;
  /**
   * Refetches the group and resolves with the observed version number (or
   * `undefined` when the refetch fails / returns no data — e.g. a transient
   * StringGroupNotReadyException while TIS is still ingesting a fresh submit).
   * Callers that must sequence work after fresh server data lands (e.g. polling
   * until a newly-published version is queryable before clearing a draft) can
   * await it and compare the returned version.
   */
  refetch: () => Promise<number | undefined>;
}

/**
 * Fetches string group metadata AND its source-string snapshot in a single
 * describeStringGroup query (includeStrings=true), which resolves against
 * TRANSCOM — the source of truth for source content and the translator context
 * note. Maps both to the local types expected by components.
 *
 * When `newGroupMetadata` is provided (the page was reached from the Create String Group
 * modal), the backend is NOT queried: the metadata is rendered straight from
 * the form data, because the group has not been created server-side at all —
 * the create mutation is not wired yet (TODO in NamespaceDetailPage), so
 * describeStringGroup has nothing to return. The first `refetch()` call (fired
 * after submit/cancel/rollback) permanently dismisses the prefill and switches
 * to server data — un-skipping the query makes Apollo run it automatically.
 */
export function useStringGroupData(
  namespaceId: string,
  stringGroupId: string,
  newGroupMetadata?: NewStringGroupMetadata | null,
): UseStringGroupDataResult {
  const [prefillDismissed, setPrefillDismissed] = useState(false);
  const prefillActive = !!newGroupMetadata && !prefillDismissed;

  const { data, loading, error, refetch } = useDescribeStringGroupQuery({
    variables: { namespaceId, stringGroupId },
    fetchPolicy: 'cache-and-network',
    skip: prefillActive,
  });

  if (prefillActive) {
    return {
      data: mapNewStringGroupMetadata(newGroupMetadata),
      // A prefilled group has no strings yet; anything the user adds lives
      // in the draft state until first submit.
      strings: [],
      loading: false,
      error: null,
      apolloError: undefined,
      // Dismissing the prefill un-skips the query for subsequent renders;
      // Apollo also executes a refetch() issued against a skipped query, so
      // this first call already returns a real observed version — letting
      // waitForPublishedVersion's polling work from a prefilled page too. A
      // failed fetch resolves to undefined ("not available yet"), matching the
      // post-prefill contract below.
      refetch: async (): Promise<number | undefined> => {
        setPrefillDismissed(true);
        return observedVersion(refetch);
      },
    };
  }

  const group = data?.describeStringGroup;

  const mapped: StringGroup | null = group
    ? {
        stringGroupId: group.stringGroupId,
        namespaceId: group.namespaceId,
        clientId: group.clientId,
        description: group.description ?? undefined,
        teamName: group.teamName ?? undefined,
        createdOn: group.createdOn ?? 0,
        createdBy: group.createdBy ?? '',
        version: group.version,
        cxFamilyId: group.cxFamilyId,
        cxIds: group.cxIds,
        sourceLocale: group.sourceLocale ?? 'en_US',
        stringCount: group.stringCount,
        updatedOn: group.updatedOn ?? 0,
        updatedBy: group.updatedBy ?? '',
        translationStatus: group.translationStatus,
        translationProgress: {
          completedLocales: group.translationProgress.completedLocales,
          inProgressLocales: group.translationProgress.inProgressLocales,
          failedLocales: group.translationProgress.failedLocales,
          cancelledLocales: group.translationProgress.cancelledLocales,
        },
      }
    : // After the prefill is dismissed the real query fires; keep rendering the
      // prefilled metadata only while that query is still in its INITIAL
      // in-flight state (loading with no response yet) so the page does not
      // blank to a full-page spinner (which would hide the flashbar carrying
      // the success message the dismissing action just queued). Once the query
      // has produced a response, a null group is authoritative (not found /
      // deleted / failed load) and must surface the same empty+error state as
      // the never-prefilled path instead of stale prefill metadata.
      newGroupMetadata && loading && !data
      ? mapNewStringGroupMetadata(newGroupMetadata)
      : null;

  const strings: StringInfo[] = group
    ? group.strings.map((s) => ({
        stringId: s.stringId,
        sourceLocale: s.sourceLocale,
        sourceText: s.sourceText,
        contextNote: s.contextNote ?? undefined,
        inlineMarkupType: s.inlineMarkupType ?? undefined,
        messageFormatType: s.messageFormatType ?? undefined,
        // Per-string clientId is not modeled; fall back to the group's client.
        clientId: group.clientId,
        variants: s.variants.map((v) => ({
          cxId: v.cxId,
          sourceText: v.sourceText,
          contextNote: v.contextNote ?? undefined,
        })),
        translationStatus:
          s.status ?? group.translationStatus ?? TranslationStatus.Complete,
      }))
    : [];

  return {
    data: mapped,
    strings,
    loading,
    error: toError(error),
    apolloError: error,
    // Resolve with the observed version so callers can poll for a freshly
    // published version. A rejected refetch (e.g. StringGroupNotReadyException
    // while TIS ingests) resolves to `undefined` rather than throwing, so it is
    // treated as "not available yet" and never surfaces as an unhandled
    // rejection; load failures are still reported via the query's own `error`.
    refetch: () => observedVersion(refetch),
  };
}

/**
 * Runs an Apollo refetch and resolves with the version it observed, or
 * `undefined` when the fetch fails or returns no data — the shared
 * "not available yet" signal both the prefill and server-backed refetch
 * paths report to version-polling callers.
 */
async function observedVersion(
  refetch: ReturnType<typeof useDescribeStringGroupQuery>['refetch'],
): Promise<number | undefined> {
  try {
    const result = await refetch();
    const v = result.data?.describeStringGroup?.version;
    return v != null ? Number(v) : undefined;
  } catch {
    return undefined;
  }
}

function toError(error: ApolloError | undefined): Error | null {
  if (!error) return null;
  return new Error(error.message);
}
